/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  createContext,
  useContext,
  useRef,
  useLayoutEffect,
  cloneElement,
} from 'react';
import { css, jsx } from '@emotion/core';
import { keyframes } from 'emotion';
import Arrow from 'components/UI/Arrow';
import { Box, Text, Flexbox, Row } from '@chameleoncreator/creator-ui';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'emotion-theming';
import { interactionContext } from './interactionContext';
import isAppleComputerInc from 'services/isAppleComputerInc';
import isAndroidSamsung from 'services/isAndroidSamsung';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { motion } from 'framer-motion';
import updateReadOnlyText from 'services/updateReadOnlyText';
import IELayout from './IELayout';
import { State } from 'store/store';
import { useEffect } from 'react';
import unlockInteraction from 'services/unlockInteraction';

const fader = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

//
const defaultBorderRadius = 6;
const highContrastBorderThickness = 3;

// Close all the accordions (used when e)
const close = (obj: {}): {} => {
  return Object.keys(obj).reduce((a, k) => {
    a[k] = false;
    return a;
  }, {});
};

const clearFieldHilites = () => {
  const fieldsClosed = Array.from(
    document.querySelectorAll('.accordion-field') as NodeList
  ) as HTMLElement[];

  fieldsClosed.forEach(field => {
    field.style.boxShadow = `none`;
  });
};

// The useAccordion hook is a state object that allows
// for accordions open/close state to be tracked and altered.
// If the exclusive tag is passed in, it will close all the
// fields when one is openned
const useAccordion = (
  unlock: () => void,
  count: number,
  exclusive?: boolean
) => {
  const [state, setState] = useState({
    open: {},
    viewed: {},
  });

  const toggle = (key: string) => {
    const past = exclusive ? close(state.open) : { ...state.open };
    const viewed = state.viewed[key.toString()]
      ? state.viewed
      : { ...state.viewed, [key.toString()]: true };

    if (Object.keys(viewed).length === count) {
      unlock();
    }

    setState({
      ...state,
      viewed,
      open: { ...past, [key]: !state.open[key] },
    });
  };

  const isOpen = (key: string) => {
    return state.open[key] || false;
  };

  return { toggle, isOpen };
};

interface AccordionCtx {
  toggle(key: string): void;
  isOpen(key: string): boolean;
}

const AccordionContext = createContext<AccordionCtx>({
  toggle: () => {},
  isOpen: () => false,
});

// A field is the grouping of headers and bodies of accordion fields.
// It passes information down the the header / body to toggle and
// open status
export const Field = ({ children, ...props }) => {
  const { toggle, isOpen } = useContext(AccordionContext);
  const {
    headingColor: borderColor,
    borderRadius = defaultBorderRadius,
    fieldBodyBackgroundColor,
  } = useContext(interactionContext).props;
  const idx = props.idx;
  const trigger = () => {
    toggle(idx);
  };

  const isFieldOpen = isOpen(idx);

  useEffect(() => {
    const onFocus = () => {
      const { activeElement } = document;

      // used for when we tab out of the accordion 'boundary'
      // if the element with focus is not a fieldHeader
      // clearFieldHilites
      if (!activeElement?.className.match('FieldHeader')) {
        clearFieldHilites();
      }
    };

    document.addEventListener('focus', onFocus, true);

    return () => {
      document.removeEventListener('focus', onFocus, true);
    };
  }, []);

  return (
    <Box
      className={`accordion-field ${isFieldOpen ? 'accordion-field-open' : ''}`}
      mt={1}
      mb={1}
      css={css({
        border: `1px solid ${borderColor || '#222'}`,
        borderRadius,
        background: fieldBodyBackgroundColor,
      })}
    >
      {React.Children.map(children, child =>
        cloneElement(child, { trigger, isOpen: isFieldOpen })
      )}
    </Box>
  );
};

interface HeaderProps {
  children: JSX.Element | string;
  foreground?: string;
  background?: string;
}

interface InjectdProps {
  isOpen: boolean;
  trigger(key: string): void;
}

// The Field Header is
export const FieldHeader = ({
  children,
  foreground = '#fff',
  background = '#222',
  ...props
}: HeaderProps) => {
  const { isOpen, trigger } = props as InjectdProps;

  const {
    headingColor,
    headingTextColor,
    borderRadius = defaultBorderRadius,
  } = useContext(interactionContext).props;

  const highContrast = useSelector((state: State) => {
    return state.theme.highContrast;
  });

  const fieldHeaderRef = useRef<HTMLElement>(null);

  const fieldFauxFocus = `0 0 0 1px #fff, 0 0 0 ${
    highContrast ? highContrastBorderThickness : 2
  }px ${headingColor}`;

  useEffect(() => {
    if (!isOpen || !fieldHeaderRef.current) {
      return;
    }

    // faux focus hilite the open field
    const fieldOpen = document.querySelector(
      '.accordion-field-open'
    ) as HTMLElement;

    if (fieldOpen) {
      fieldOpen.style.boxShadow = fieldFauxFocus;
    }
  }, [isOpen]);

  return (
    <ThemeProvider theme={{ Text: { color: headingTextColor || foreground } }}>
      <Flexbox
        ref={fieldHeaderRef}
        as="button"
        border="none"
        bg={headingColor || background}
        flexDirection="row"
        alignItems="center"
        css={css`
          position: relative;
          left: -1px;
          width: calc(100% + 2px);

          .chameleon-text {
            padding: 10px ${window.dev() ? '5px' : '7px'};
          }

          border-radius: ${isOpen
            ? `${borderRadius}px ${borderRadius}px 0 0`
            : `${borderRadius}px`};

          transition: border-radius ${0.225 / 1.5}s;

          ${!isOpen &&
            `
          &:focus {
            box-shadow: ${fieldFauxFocus};
            
          }
          `}

          cursor: pointer;
        `}
        onFocus={e => {
          clearFieldHilites();

          if (isOpen) {
            const fieldOpen = document.querySelector(
              '.accordion-field-open'
            ) as HTMLElement;

            if (fieldOpen) {
              //
              fieldOpen.style.boxShadow = fieldFauxFocus;
              //
            }
          }
        }}
        onClick={e => {
          trigger('');
        }}
        onKeyUp={e => {
          if (e.keyCode === 32) {
            e.currentTarget.click();
          }
        }}
        p={2}
        aria-expanded={isOpen}
      >
        <Text
          as="div"
          flex="1"
          textAlign="left"
          css={css`
            * div {
              color: ${headingTextColor};
            }

            p {
              // line-height: auto !important;
            }
          `}
        >
          {children}
        </Text>
        <Box flex={0} p={1} pl={3} pr={2}>
          <Arrow
            color={headingTextColor || '#fff'}
            rotate={isOpen ? 90 : 0}
            delay={isOpen ? 0.25 : 0}
          />
        </Box>
      </Flexbox>
    </ThemeProvider>
  );
};

// This posed element is responsible for the transition between open and closed states
// const Collapse = posed.div({
//   closed: { height: 0 },
//   open: { height: 'auto' },
// });

export const FieldBody = ({ children, ...props }) => {
  const { isOpen } = props as InjectdProps;

  const { id } = props;

  const fieldBody = useRef<HTMLDivElement>(null);

  const {
    fieldBodyBackgroundColor,
    fieldBodyTextColor,
    borderRadius = 0,
  } = useContext(interactionContext).props;

  //
  // var it for testing . . .
  const isDevice = isAppleComputerInc() || isAndroidSamsung();

  if (window.dev && !window.dev() && isDevice) {
    const IsAppleAndroid = ({ children }) => {
      useLayoutEffect(() => {
        const rowFieldBody = document.querySelector(`#i${id}`);
        rowFieldBody && updateReadOnlyText(rowFieldBody);
      }, []);

      return (
        <Row
          id={`i${id}`}
          className={isOpen ? 'fader_1s' : ''}
          css={css`
            height: ${isOpen ? 'auto' : 0};
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
            background: ${fieldBodyBackgroundColor};

            p,
            li > span {
              color: ${fieldBodyTextColor};
            }

            overflow: hidden;
          `}
          aria-hidden={!isOpen}
          aria-live="assertive"
        >
          {children}
        </Row>
      );
    };

    return <IsAppleAndroid>{children}</IsAppleAndroid>;
  }

  return (
    <motion.div
      ref={fieldBody}
      id={`i${id}`}
      style={{
        display: 'none',
        overflow: 'hidden',
        borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
        background: fieldBodyBackgroundColor,
      }}
      initial={{ height: 0, opacity: 0 }}
      transition={{
        duration: 0.225,
        ease: 'easeOut',
      }}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      onAnimationStart={() => {
        if (isOpen && fieldBody.current) {
          fieldBody.current.style.display = '';
          // fieldBody.current.style.borderRadius = `${borderRadius}px`;
        }
      }}
      onAnimationComplete={() => {
        if (!isOpen && fieldBody.current) {
          fieldBody.current.style.display = 'none';
        }
      }}
      aria-hidden={!isOpen}
      aria-live="assertive"
    >
      <Row
        css={css`
          p,
          li > span {
            color: ${fieldBodyTextColor};
          }
        `}
      >
        {children}
      </Row>
    </motion.div>
  );
};

// An accordion is made up of 1 or more children
interface AccordionProps {
  children: JSX.Element[] | JSX.Element;
}

export const Accordion = ({ children }: AccordionProps) => {
  const iContext = useContext(interactionContext);
  const interactionsLocked = useSelector((state: State) => {
    return state.interactionsLocked;
  });

  const block = getBlockById(iContext.id);

  // made this tweak here to fix the issue anglia revealed
  // problem is i'm kinda sure this was different code at some earlier point
  // works now. all good. but might want to chat with sam
  // . . .
  const unlock = () => {
    if (window.dev && window.dev()) {
      return;
    }

    //
    // if (block.props!.lockDown === 0) {
    //   return;
    // }
    // setBlockProps(block, { lockDown: 0 });

    if (!interactionsLocked?.includes(iContext.id)) {
      return;
    }

    unlockInteraction(iContext.id);
  };

  const ctx = useAccordion(unlock, React.Children.count(children), true);

  return (
    <AccordionContext.Provider value={{ ...ctx }}>
      {React.Children.map(children, (c, i) => cloneElement(c, { idx: i }))}
    </AccordionContext.Provider>
  );
};
